import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import moment from 'moment';
import { Skeleton, Timeline } from '@mantine/core';
import { LuHotel } from 'react-icons/lu';
import { RiClockwise2Line } from 'react-icons/ri';
import { TbCreditCardRefund } from 'react-icons/tb';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import { MdOutlineFreeCancellation, MdOutlineMoneyOff, MdOutlinePending } from 'react-icons/md';

import { EDrawer } from 'libs/ui';
import { useHotelData } from 'libs/data-access';
import { humanizeText, parseLocalDateTime, showToast } from 'utils/utility';
import { IBookingTimeline, IHotelOnlineBookingList } from 'libs/data-access/types';

interface IProps {
    hotelTicket: IHotelOnlineBookingList;
    opened: boolean;
    onClose: Dispatch<SetStateAction<boolean>>;
};

export default function HotelTicketTimeline({ ...props }: IProps) {

    const { getHotelData } = useHotelData();

    const [timeline, setTimeline] = useState<IBookingTimeline[]>([]);
    const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(false);


    useEffect(() => {
        props.opened && getTimeline();
    }, [props.opened]);


    // API CALLS
    const getTimeline = async () => {

        setIsLoadingTimeline(true);

        const response = await getHotelData(`/booking/${props.hotelTicket.id}/booking-status-progress`);

        if (response.isSuccess) {
            // console.log("Response for getting timeline: ", response.data?.data);
            setTimeline(response.data?.data);
        } else {
            console.log("Error while getting timeline: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting timeline!"
            });
        };

        setIsLoadingTimeline(false);

    };


    return (
        <EDrawer
            size="lg"
            opened={props.opened}
            onClose={() => props.onClose(false)}
            title={
                <div className="flex items-center gap-2">
                    <LuHotel size={30} />
                    <div className="text-lg font-semibold">
                        {`${props.hotelTicket.referenceNo || ""} | ${props.hotelTicket.bookingRooms?.[0]?.roomName || ""}`}
                    </div>
                </div>
            }
        >
            {
                isLoadingTimeline
                    ?
                    <div className="flex flex-col gap-5">
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                        <Skeleton height={70} radius={"md"} />
                    </div>
                    :
                    <div>
                        <Timeline bulletSize={30} lineWidth={3} radius={"md"}>
                            {
                                timeline.map((each, index) => (
                                    <Timeline.Item
                                        key={index + "_h_timeline"}
                                        lineVariant="solid"
                                        bullet={
                                            each.bookingStatus === "SUCCESS"
                                                ?
                                                <IoIosCheckmarkCircleOutline size={22} color="green" />
                                                :
                                                each.bookingStatus === "FAILED"
                                                    ?
                                                    <MdOutlinePending size={22} color="red" />
                                                    :
                                                    each.bookingStatus === "PENDING"
                                                        ?
                                                        <MdOutlineMoneyOff size={22} color="orange" />
                                                        :
                                                        each.bookingStatus === "CANCELLED"
                                                            ?
                                                            <MdOutlineFreeCancellation size={22} color="red" />
                                                            :
                                                            each.bookingStatus === "TO_BE_REFUNDED"
                                                                ?
                                                                <TbCreditCardRefund size={22} color="orange" />
                                                                :
                                                                each.bookingStatus === "REFUNDED"
                                                                    ?
                                                                    <RiClockwise2Line size={22} color="violet" />
                                                                    :
                                                                    <IoIosCheckmarkCircleOutline size={22} />
                                        }
                                        title={
                                            <div className="flex items-center gap-2">
                                                <span className={
                                                    `text-sm font-semibold ${each.bookingStatus === "SUCCESS"
                                                        ?
                                                        "text-green-600"
                                                        :
                                                        each.bookingStatus === "FAILED"
                                                            ?
                                                            "text-red-600"
                                                            :
                                                            each.bookingStatus === "PENDING"
                                                                ?
                                                                "text-teal-600"
                                                                :
                                                                each.bookingStatus === "CANCELLED"
                                                                    ?
                                                                    "text-red-600"
                                                                    :
                                                                    each.bookingStatus === "TO_BE_REFUNDED"
                                                                        ?
                                                                        "text-yellow-600"
                                                                        :
                                                                        each.bookingStatus === "REFUNDED"
                                                                            ?
                                                                            "text-violet-600"
                                                                            :
                                                                            "text-gray-600"
                                                    }`
                                                }
                                                >{humanizeText(each.bookingStatus)}</span>
                                                <span className="text-sm">{parseLocalDateTime(each.createdAt)}</span>
                                            </div>
                                        }
                                    >
                                        <div>
                                            {
                                                each?.systemGenerated
                                                    ?
                                                    <span className="text-sm text-slate-500">System Generated</span>
                                                    :
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-slate-500">{each.name}, {each.email}, ({humanizeText(each.userType)})</span>
                                                        {each.remark ? <span className="text-sm text-slate-500">Remarks: {each.remark}</span> : <></>}
                                                    </div>
                                                // <span className="text-sm text-slate-500">{each.name}, {each.email}, {humanizeText(each.userType)}</span>
                                            }
                                        </div>
                                    </Timeline.Item>
                                ))
                            }
                        </Timeline>
                    </div>
            }
        </EDrawer>
    )
}